// src/services/push.js
import eventHandlerManager from 'cov-jitux-push-event-handler';
import Config from "./config";
import { context } from "./context";

// Convert base64 string to Uint8Array for the applicationServerKey
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/\-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Function to decode JWT and get username
function getUsernameFromToken(token) {
  try {
    // Get the payload part of the JWT (second part)
    const payloadBase64 = token.split('.')[1];
    
    // Base64 decode and parse as JSON
    const payload = JSON.parse(atob(payloadBase64));
    
    // Extract username (could be in different fields depending on your JWT structure)
    return payload.user_name || payload.sub || payload.email || 'Unknown user';
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
}


// Push Notification Service
class Push {
  constructor() {
    this.serviceWorkerRegistration = null;
    this.subscription = null;
    this.isSubscribed = false;
    this.notificationListeners = [];
    this.statusListeners = [];
    this.username = null;
    this.statusIndicator = null;
    this.healthCheckInterval = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 5000; // Start with 5 seconds
    this.serverAvailable = true;
  }

  checkSMSCapabilities() {
    const capabilities = {
      webShare: 'share' in navigator,
      contacts: 'contacts' in navigator,
      credentials: 'credentials' in navigator,
      webSMS: false, // Web SMS API isn't standardized
      serviceWorker: 'serviceWorker' in navigator,
      pushManager: 'PushManager' in window,
      notifications: 'Notification' in window,
      permissions: 'permissions' in navigator,
      mobileDevice: this._isMobileDevice(),
      androidDevice: this._isAndroidDevice(),
      iosDevice: this._isIOSDevice(),
      pwaInstalled: window.matchMedia('(display-mode: standalone)').matches
    };
    
    // Check if SMS-related permissions are available
    if (capabilities.permissions) {
      navigator.permissions.query({ name: 'contacts' })
        .then(permissionStatus => {
          capabilities.contactsPermission = permissionStatus.state;
        })
        .catch(() => {
          capabilities.contactsPermission = 'unsupported';
        });
    }
    
    // Check if the Web Share API can share text (potential SMS alternative)
    if (capabilities.webShare) {
      capabilities.canShareText = navigator.canShare && 
        navigator.canShare({ text: 'test' });
    }
    
    // Determine overall SMS capability based on device and features
    capabilities.potentialSMSCapability = 
      capabilities.mobileDevice && 
      (capabilities.androidDevice || capabilities.iosDevice);
    
    return capabilities;
  }

  // Helper methods for device detection
  _isMobileDevice() {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
      (navigator.maxTouchPoints && navigator.maxTouchPoints > 2 && /Mobi/i.test(navigator.userAgent));
  }

  _isAndroidDevice() {
    return /Android/i.test(navigator.userAgent);
  }

  _isIOSDevice() {
    return /iPhone|iPad|iPod/i.test(navigator.userAgent) || 
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  }

  // Initialize the service
  async init(clientId, applicationCd) {    
    let authToken = await this.getAuthToken();
    if ( !authToken ) {
       console.log('Skipping as user is not logged in');
      return null;
    }

    await Config.loadConfig();    
    
    let enabled = await this.checkInAppNotificationsEnabled(clientId, applicationCd);
    if ( !enabled ) {
      console.log('Notifications disabled at config level');
      return null;
    }
    // Create status indicator element
    this._createStatusIndicator();
    
    // Check SMS capabilities
    const smsCapabilities = this.checkSMSCapabilities();
    console.log('SMS capabilities:', smsCapabilities);
    
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      this._notifyStatusChange('Push notifications are not supported in this browser');
      this._updateStatusIndicator(false);
      return false;
    }

    try {
      // Register the service worker
      const registration = await navigator.serviceWorker.register('./dist/service-worker.js');
      console.log('Service Worker registered with scope:', registration.scope);
      this.serviceWorkerRegistration = registration;
      
      // Set up message listener for communication from service worker
      navigator.serviceWorker.addEventListener('message', this._handleServiceWorkerMessage.bind(this));
      this.updateServiceWorker(registration);
      
      // Check subscription status and update indicator
      await this.checkSubscriptionStatus(registration);
      
      // Start health monitoring
      this.startHealthMonitoring();
      
      return registration;
    } catch (error) {
      console.error('Service Worker registration failed:', error);
      this._notifyStatusChange(`Error registering service worker: ${error.message}`);
      this._updateStatusIndicator(false);
      return null;
    }
  }

  // Get authentication token
  async getAuthToken() {
  let token = null;
  
  // First check if we have the context in localStorage
  let contextStr = localStorage.getItem('jit-context');
  
  // If no context found in localStorage, try sessionStorage
  if (!contextStr) {
    contextStr = sessionStorage.getItem('jit-context');
  }
  
  // Parse the context if found
  if (contextStr) {
    try {
      const context = JSON.parse(contextStr);
      // Extract token from identity.accessToken path
      if (context && context.identity && context.identity.accessToken) {
        token = context.identity.accessToken;
        console.log('Found token in jit-context');
      }
    } catch (e) {
      console.error('Error parsing jit-context:', e);
    }
  }
  
  return token;
}
  /**
   * Fetches the current user client configuration
   * @returns {Promise<Object>} The client configuration JSON object
   */
    async fetchCurrentUserClientConfig(clientId, applicationCd) {
      try {  
        let configUrl = Config.get('appClientConfigUrl');
        let configUrlProcessed = this.substitute(configUrl, {clientId: clientId, applicationCd: applicationCd});
        const response = await fetch(configUrlProcessed, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
            // Add any auth headers if needed
          },
          credentials: 'include' // Include cookies if using session auth
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
                
        return await response.json();
      } catch (error) {
        console.error('Error fetching client config:', error);
        throw error;
      }
    }

  async checkInAppNotificationsEnabled(clientId, applicationCd) {
    try {
      // Check if push is enabled in the config
      if (Config.pushEnabled) {     
        // Fetch client configuration
        let clientConfig = await this.fetchCurrentUserClientConfig(clientId, applicationCd);
        
        // Check if config exists and has notificationType array
        if (clientConfig && clientConfig.config && (clientConfig.config.notifications || "").toLowerCase() === "enabled") {
          // Check if "In-App" is in the array
          const inAppNotificationsEnabled = true
          console.log('In-app notifications enabled:', inAppNotificationsEnabled);
          return inAppNotificationsEnabled;
        }
        
        console.log('In-app notifications not found in config');
        return false;
      } else {
        console.log('Push notifications disabled in Config');
        return false;
      }
    } catch (error) {
      console.error('Error checking in-app notifications config:', error);
      this.serverAvailable = false;
      return false;
    }
  }


 
  substitute(template, values) {
    if (template) {
      return template.replace(/\${(.*?)}/g, (match, placeholder) => {
        return values[placeholder.trim()] || "";
      });
    }
  }
 

  attemptSMSFallback(phoneNumber, message) {
    const capabilities = this.checkSMSCapabilities();
    
    if (capabilities.mobileDevice) {
      // On mobile devices, we can try to open the SMS app
      let smsUrl;
      
      if (capabilities.androidDevice) {
        // Android SMS URI
        smsUrl = `sms:${phoneNumber}?body=${encodeURIComponent(message)}`;
      } else if (capabilities.iosDevice) {
        // iOS SMS URI
        smsUrl = `sms:${phoneNumber}&body=${encodeURIComponent(message)}`;
      }
      
      if (smsUrl) {
        window.location.href = smsUrl;
        return true;
      }
    } else if (capabilities.webShare) {
      // Try using Web Share API as fallback
      navigator.share({
        title: 'Message',
        text: message
      })
      .then(() => console.log('Shared successfully'))
      .catch(err => console.error('Error sharing:', err));
      
      return true;
    }
    
    return false;
  }

  // Create status indicator element
  _createStatusIndicator() {
    // Remove existing indicator if present
    if (this.statusIndicator) {
      document.body.removeChild(this.statusIndicator);
    }
    
    // Create new indicator
    this.statusIndicator = document.createElement('div');
    
    // Style the indicator - positioned in bottom right
    Object.assign(this.statusIndicator.style, {
      position: 'fixed',
      bottom: '10px',
      right: '30px',  // Changed from left to right
      width: '16px',
      height: '16px',
      borderRadius: '50%',
      backgroundColor: '#ccc',  // Default gray color
      border: '1px solid #999',
      zIndex: '9999',
      transition: 'background-color 0.3s ease',
      cursor: 'pointer',
      boxShadow: '0 0 5px rgba(0,0,0,0.3)'
    });
    
    // Add tooltip
    this.statusIndicator.title = 'Push notification status';
    
    // Add click handler to toggle subscription
    this.statusIndicator.addEventListener('click', async () => {
      try {
        if (this.isSubscribed) {
          await this.unsubscribe();
        } else {
          await this.subscribe(this.serviceWorkerRegistration);
        }
      } catch (error) {
        console.error('Error toggling subscription:', error);
      }
    });
    
    // Add to document
    document.body.appendChild(this.statusIndicator);
  }

  // Update status indicator
  _updateStatusIndicator(isActive, state = 'normal') {
    if (!this.statusIndicator) return;
    
    if (isActive) {
      if (state === 'warning') {
        this.statusIndicator.style.backgroundColor = '#FFA500';  // Orange for warning
        this.statusIndicator.title = 'Push notifications active, but server connection issues. Click for details.';
      } else {
        this.statusIndicator.style.backgroundColor = '#4CAF50';  // Green
        this.statusIndicator.title = 'Push notifications active. Click to disable.';
      }
    } else {
      this.statusIndicator.style.backgroundColor = '#ccc';  // Gray
      this.statusIndicator.title = 'Push notifications inactive. Click to enable.';
    }
  }

  // Handle messages from service worker
  _handleServiceWorkerMessage(event) {
    if (event.data && event.data.type === 'PUSH_RECEIVED') {
      console.log('Push notification received:', event.data.notification);
      this._notifyNotificationReceived(event.data.notification);
    }
  }

  // Check current subscription status
  async checkSubscriptionStatus(registration) {
    try {
      //const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        console.log('User is subscribed');
        this.subscription = subscription;
        this.isSubscribed = true;
        this._notifyStatusChange('Currently subscribed to push notifications');
        this._updateStatusIndicator(true);
      } else {
        console.log('User is NOT subscribed');
        this.subscription = null;
        this.isSubscribed = false;
        this._notifyStatusChange('Not subscribed to push notifications');
        this._updateStatusIndicator(false);
      }
      
      return this.isSubscribed;
    } catch (error) {
      console.error('Error checking subscription:', error);
      this._notifyStatusChange(`Error checking subscription status: ${error.message}`);
      this._updateStatusIndicator(false);
      return false;
    }
  }

  updateServiceWorker(registration) {
    if ('serviceWorker' in navigator) {
      registration.update()
          .then(() => {
            console.log('Service worker update checked');
          })
          .catch(error => {
            console.error('Service worker update failed:', error);
          });    
    }
  }

  // Subscribe to push notifications
  async subscribe(registration) {
    try {
      this._notifyStatusChange('Subscribing...');
      
      // Subscribe the user
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(Config.publicVapidKey)
      });
      
      console.log('Subscription info:', subscription);
      
      // Get the authentication token
      let authToken = await this.getAuthToken();
      
      if (authToken) {                      
        // Extract username from JWT
        this.username = getUsernameFromToken(authToken);
        
        // Send subscription to server with timeout to detect server issues
        let response;
        try {
          response = await fetch(Config.pushUrl+'/subscribe', {
            method: 'POST',
            body: JSON.stringify({ subscription }),
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${authToken}`
            },
            // Set a timeout to detect server issues
            signal: AbortSignal.timeout(10000)
          });
        } catch (fetchError) {
          // Handle network errors or timeouts
          this.serverAvailable = false;
          console.error('Server connection error:', fetchError);
          
          // Store subscription locally even if server is down
          this.subscription = subscription;
          this.isSubscribed = true;
          this._notifyStatusChange('Subscribed locally, but server is unreachable');
          this._updateStatusIndicator(true, 'warning');
          
          // Start reconnection process
          this.resubscribe();
          
          return subscription;
        }
      
      
        // Check if token is expired (401 Unauthorized)
        if (response.status === 401 || response.status === 403) {
          console.log('Token expired, requesting new token');
          
          // Clear existing tokens from jit-context
          localStorage.removeItem('jit-context');
          sessionStorage.removeItem('jit-context');
          
          // Request new token
          authToken = await this.getAuthToken();      
          
          // Extract username from new JWT
          this.username = getUsernameFromToken(authToken);
          
          // Retry the request with new token
          response = await fetch(Config.pushUrl+'/subscribe', {
            method: 'POST',
            body: JSON.stringify({ subscription }),
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${authToken}`
            },
          });
        }
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error(`Server error (${response.status}): ${errorText}`);
        }
        
        this.subscription = subscription;
        this.isSubscribed = true;
        this.serverAvailable = true;
        this._notifyStatusChange('Successfully subscribed to push notifications');
        this._updateStatusIndicator(true);
      }
      
      return subscription;
    } catch (error) {
      console.error('Error subscribing to push notifications:', error);
      this._notifyStatusChange(`Error subscribing: ${error.message}`);
      this._updateStatusIndicator(false);      
    }
  }

  // Unsubscribe from push notifications
  async unsubscribe() {
    try {
      this._notifyStatusChange('Unsubscribing...');
      
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        // Get the authentication token
        const authToken = await this.getAuthToken();
        
        if (authToken) {
          
          // Send unsubscribe request to server first
          try {
            const response = await fetch(Config.pushUrl+'/unsubscribe', {
              method: 'DELETE',
              body: JSON.stringify({ endpoint: subscription.endpoint }),
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${authToken}`
              },
              signal: AbortSignal.timeout(10000)
            });
            
            if (!response.ok) {
              const errorText = await response.text();
              console.error('Server error during unsubscribe:', errorText);
            }
          } catch (fetchError) {
            console.error('Server connection error during unsubscribe:', fetchError);
            this.serverAvailable = false;
          }
        }
        
        // Then unsubscribe locally
        await subscription.unsubscribe();
        this.subscription = null;
        this.isSubscribed = false;
        this._notifyStatusChange('Successfully unsubscribed');
        this._updateStatusIndicator(false);
      }
    } catch (error) {
      console.error('Error unsubscribing:', error);
      this._notifyStatusChange(`Error unsubscribing: ${error.message}`);
      // Keep indicator state unchanged on error
    }
  }

  // Send a test notification
  async sendTestNotification(title, message) {
  try {
    // Get the authentication token
    const authToken = await this.getAuthToken();
    
    if (!authToken) {
      console.error('Authentication token not available');
      return null;
    }
    
    const response = await fetch(Config.pushUrl+'/publish', {
      method: 'POST',
      body: JSON.stringify({
        title,
        message,
        data: { source: 'test-ui' }
      }),
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`
      },
      signal: AbortSignal.timeout(10000)
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Server error (${response.status}): ${errorText}`);
      return null;
    }
    
    const result = await response.json();
    console.log('Test notification sent:', result);
    return result;
  } catch (error) {
    console.error('Error sending test notification:', error);
    this.serverAvailable = false;
    return null;
  }
}

  // Add status change listener
  addStatusListener(callback) {
    this.statusListeners.push(callback);
    return () => {
      this.statusListeners = this.statusListeners.filter(cb => cb !== callback);
    };
  }

  // Notify all status listeners
  _notifyStatusChange(status) {
    this.statusListeners.forEach(callback => {
      try {
        callback(status);
      } catch (error) {
        console.error('Error in status listener:', error);
      }
    });
  }

  
  // Update the _notifyNotificationReceived method to filter by application
  _notifyNotificationReceived(notification) {
    // Extract the application identifier from the notification
    const notificationApp = notification && notification.application;
    
    if (!notificationApp) {
      console.warn('Received notification without application identifier:', notification);
    }
    
    let eventHandlers = eventHandlerManager.getEventHandlers(notification.application, notification.type);
    eventHandlers.forEach(listener => {
      try {
        // Only notify listeners that match the application ID or listeners without app filtering        
        listener.callback(notification);        
      } catch (error) {
        console.error('Error in notification listener:', error);
      }
    });
  }
  

  // Get current status
  getStatus() {
  return {
    isSubscribed: this.isSubscribed,
    subscription: this.subscription,
    username: this.username,
    isSupported: 'serviceWorker' in navigator && 'PushManager' in window,
    serverAvailable: this.serverAvailable,
    smsCapabilities: this.checkSMSCapabilities()
  };
}

  // Check server health
  async checkServerHealth() {
    try {
      const response = await fetch(Config.pushUrl+'/health', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        },
        // Set a timeout to avoid long waits
        signal: AbortSignal.timeout(5000)
      });
      
      if (response.ok) {
        if (!this.serverAvailable) {
          console.log('Push server is back online');
          this._notifyStatusChange('Push server connection restored');
          this.serverAvailable = true;
          this.reconnectAttempts = 0;
          
          // If we're supposed to be subscribed but aren't, resubscribe
          if (this.isSubscribed && !this.subscription) {
            await this.resubscribe();
          }
        }
        return true;
      } else {
        console.error(`Server returned ${response.status}`);
      }
    } catch (error) {
      if (this.serverAvailable) {
        console.error('Push server appears to be down:', error);
        this._notifyStatusChange('Push server connection lost');
        this.serverAvailable = false;
        this._updateStatusIndicator(this.isSubscribed, 'warning');
      }
      return false;
    }
  }

  // Start health monitoring
  startHealthMonitoring() {
    // Clear any existing interval
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval);
    }
    
    // Check immediately
    this.checkServerHealth();
    
    // Then set up regular checks
    this.healthCheckInterval = setInterval(() => {
      this.checkServerHealth();
    }, 30000); // Check every 30 seconds
  }

  // Stop health monitoring
  stopHealthMonitoring() {
    if (this.healthCheckInterval) {
      clearInterval(this.healthCheckInterval);
      this.healthCheckInterval = null;
    }
  }

  // Reconnection method with exponential backoff
  async resubscribe() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      this._notifyStatusChange('Max reconnection attempts reached');
      return false;
    }
    
    try {
      this._notifyStatusChange(`Attempting to reconnect (${this.reconnectAttempts + 1}/${this.maxReconnectAttempts})...`);
      
      // Wait for the service worker to be ready
      const registration = await navigator.serviceWorker.ready;
      
      // Try to subscribe
      await this.subscribe(registration);
      
      this._notifyStatusChange('Successfully reconnected to push service');
      this.reconnectAttempts = 0;
      return true;
    } catch (error) {
      this.reconnectAttempts++;
      
      // Calculate delay with exponential backoff (5s, 10s, 20s, 40s, 80s)
      const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);
      
      console.log(`Reconnection attempt failed. Retrying in ${delay/1000} seconds...`);
      this._notifyStatusChange(`Reconnection failed. Retrying in ${delay/1000} seconds...`);
      
      // Schedule next attempt
      setTimeout(() => this.resubscribe(), delay);
      
      return false;
    }
  }

  // Cleanup
  cleanup() {
    // Stop health monitoring
    this.stopHealthMonitoring();
    
    // Remove service worker message listener
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.removeEventListener('message', this._handleServiceWorkerMessage);
    }
    
    // Remove status indicator
    if (this.statusIndicator && this.statusIndicator.parentNode) {
      this.statusIndicator.parentNode.removeChild(this.statusIndicator);
    }
    
    // Clear all listeners
    this.notificationListeners = [];
    this.statusListeners = [];
  }
}

// Create singleton instance
const push = new Push();

export default push;